/**
 * Eine Notiz der Band-Bibel als unendliche Flaeche.
 *
 * Die Flaeche selbst steckt in features/canvas/board-canvas.ts. Hier kommen
 * nur der Rahmen dazu: Brotkrumen, die Werkzeugleiste und das automatische
 * Speichern. Einen Knopf zum Speichern gibt es bewusst nicht.
 *
 * Handschrift kommt nicht von hier, sondern von der gemeinsamen Zeichenebene:
 * die View meldet nur host, content und die Einheit (den Zoomfaktor).
 *
 * WICHTIG zur Werkzeugleiste
 *   Sie wird EINMAL gebaut; bei einer neuen Auswahl werden nur Gruppen ein-
 *   und ausgeblendet und Werte gesetzt. Wuerde sie jedes Mal neu entstehen,
 *   flogen die Farbfelder aus dem Dokument – und iPadOS schliesst den
 *   Farbwaehler des Systems, sobald das zugehoerige Feld verschwindet.
 *   Genau dieser Fehler ist uns bei der Stiftpalette schon einmal passiert.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Board, CanvasBackground, CanvasEdge, CanvasObject } from '../types.ts';
import {
  boardSurfaceId,
  boardTitle,
  defaultFontSize,
  deleteBoard,
  getBoard,
  getRoom,
  saveBoard,
} from '../db/repo.ts';
import { createBoardCanvas } from '../features/canvas/board-canvas.ts';
import { AUDIO_ACCEPT, readAudio } from '../features/canvas/audio.ts';
import { button, h } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { toast } from '../ui/toast.ts';
import { askText } from '../ui/ask.ts';

/** Wie lange nach der letzten Aenderung gespeichert wird. */
const SAVE_DELAY = 700;

/** Grenzen der Schriftgroesse in Weltpixeln. */
const MIN_FONT = 9;
const MAX_FONT = 72;

/** Was man auf die Flaeche legen kann. */
const PIECES: { type: CanvasObject['type']; label: string; hint: string }[] = [
  { type: 'text', label: 'Text', hint: 'Freier Text ohne Rahmen' },
  { type: 'card', label: 'Karte', hint: 'Notizzettel mit Rahmen' },
  { type: 'node', label: 'Knoten', hint: 'Baustein für die Mindmap' },
  { type: 'checklist', label: 'Liste', hint: 'Punkte zum Abhaken' },
  { type: 'shape', label: 'Form', hint: 'Rechteck oder Ellipse' },
];

/** Arten, bei denen Schriftgestaltung etwas bewirkt. */
const TEXTY = new Set<CanvasObject['type']>(['text', 'card', 'node', 'checklist', 'shape']);

const RASTERS: { value: CanvasBackground; label: string }[] = [
  { value: 'plain', label: 'Kein Raster' },
  { value: 'lines', label: 'Linien' },
  { value: 'grid', label: 'Kariert' },
  { value: 'dots', label: 'Punkte' },
];

export async function renderBibleBoard(params: Record<string, string>): Promise<View> {
  const existing = await getBoard(params.id as string);
  if (!existing) {
    return {
      title: 'Fläche nicht gefunden',
      back: '#/bible',
      body: h('div', { class: 'empty', text: 'Diese Fläche wurde geloescht.' }),
    };
  }

  let board: Board = existing;
  const room = await getRoom(board.roomId);

  let saveTimer = 0;
  let pending: Board | null = null;

  const status = h('span', { class: 'topbar__sub', text: 'Gesichert' });

  /** Schreibt den letzten Stand in die Datenbank. */
  async function flush(): Promise<void> {
    if (!pending) return;
    const next = pending;
    pending = null;
    board = await saveBoard(next);
    status.textContent = 'Gesichert';
  }

  function queue(next: Board): void {
    // Titel und Reihenfolge bleiben, was sie sind – die Flaeche kennt sie nicht.
    pending = { ...board, ...next, title: board.title, order: board.order };
    status.textContent = 'Änderungen …';
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => void flush(), SAVE_DELAY);
  }

  /* ------------------------------------------------------------ Flaeche */

  const canvas = createBoardCanvas({
    board,
    onChange: queue,
    onSelect: (object) => syncBar(object, null),
    onEdgeSelect: (edge) => syncBar(null, edge),
  });

  /* ------------------------------------------------ Dateien einlesen */

  /** Oeffnet die Dateiauswahl und liefert die gewaehlte Datei. */
  function pickFile(accept: string): Promise<File | null> {
    return new Promise((resolve) => {
      const input = h('input', {
        attrs: { type: 'file', accept },
        style: { display: 'none' },
      });
      input.addEventListener('change', () => {
        const file = input.files?.[0] ?? null;
        input.remove();
        resolve(file);
      });
      document.body.append(input);
      input.click();
    });
  }

  /** Bild vom Geraet holen und als Data-URL ablegen – offline, ohne Server. */
  async function addImage(): Promise<void> {
    const file = await pickFile('image/*');
    if (!file) return;
    const src = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(String(reader.result ?? '')));
      reader.addEventListener('error', () => resolve(''));
      reader.readAsDataURL(file);
    });
    if (src === '') {
      toast('Bild konnte nicht gelesen werden');
      return;
    }
    warnIfLarge(src);
    canvas.addObject({ type: 'image', src, name: file.name });
  }

  /** Tonaufnahme einlesen: Data-URL, Laenge und Wellenform in einem Schritt. */
  async function addAudio(): Promise<void> {
    const file = await pickFile(AUDIO_ACCEPT);
    if (!file) return;
    toast('Aufnahme wird gelesen …');
    try {
      const info = await readAudio(file);
      warnIfLarge(info.src);
      canvas.addObject({
        type: 'audio',
        src: info.src,
        name: info.name,
        duration: info.duration,
        peaks: info.peaks,
      });
      if (info.peaks.length === 0) {
        // Ehrlich sagen, was fehlt: abspielen geht, nur die Wellenform nicht.
        toast('Eingefügt – dieses Format lässt sich nicht zeichnen');
      }
    } catch {
      toast('Diese Datei konnte nicht gelesen werden');
    }
  }

  /** Grosse Dateien blaehen die Datenbank und das Backup auf. */
  function warnIfLarge(src: string): void {
    if (src.length > 6_000_000) toast('Grosse Datei – das Backup wird dadurch dick.');
  }

  /* ------------------------------------------------- Werkzeugleiste */

  /** Ein Farbfeld mit dem Farbwähler des Systems. */
  function colorField(
    label: string,
    apply: (value: string) => void,
  ): { wrap: HTMLElement; input: HTMLInputElement } {
    const input = h('input', {
      class: 'board-bar__color',
      attrs: { type: 'color', 'aria-label': label, title: label },
    });
    // "input" statt "change": die Farbe soll schon beim Ziehen im Rad wirken.
    input.addEventListener('input', () => apply(input.value));
    const wrap = h(
      'label',
      { class: 'board-bar__field', attrs: { title: label } },
      h('span', { class: 'board-bar__field-label', text: label }),
      input,
    );
    return { wrap, input };
  }

  /** Wandelt eine Themefarbe in einen Wert, den ein Farbfeld anzeigen kann. */
  function asHex(value: string | undefined, fallback: string): string {
    return value && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
  }

  const bar = h('div', { class: 'board-bar' });

  /* --- Gruppe: Einfuegen (immer sichtbar) --- */

  const insertGroup = h('div', { class: 'board-bar__group' });
  for (const piece of PIECES) {
    insertGroup.append(
      button(piece.label, () => canvas.addObject({ type: piece.type }), {
        class: 'board-bar__btn',
        title: piece.hint,
      }),
    );
  }
  insertGroup.append(
    button('Bild', () => void addImage(), { class: 'board-bar__btn', title: 'Bild einfügen' }),
    button('Ton', () => void addAudio(), {
      class: 'board-bar__btn',
      title: 'Tonaufnahme einfügen (MP3, M4A, WAV, AIFF …)',
    }),
  );

  /* --- Gruppe: Raster der Flaeche --- */

  const rasterSelect = h('select', {
    class: 'board-bar__select',
    attrs: { 'aria-label': 'Hintergrund' },
  });
  for (const raster of RASTERS) {
    rasterSelect.append(h('option', { text: raster.label, attrs: { value: raster.value } }));
  }
  rasterSelect.value = canvas.background();
  rasterSelect.addEventListener('change', () =>
    canvas.setBackground(rasterSelect.value as CanvasBackground),
  );

  /*
   * Das Papier gilt fuer die ganze Flaeche, nicht fuer ein Objekt – deshalb
   * sitzt es oben bei den Dokumentaktionen und nicht unten in der Leiste.
   * Dort waere es ausserdem nur erreichbar, solange nichts gewaehlt ist.
   */
  const rasterField = h(
    'label',
    { class: 'topbar__field', attrs: { title: 'Hintergrund der Fläche' } },
    h('span', { class: 'topbar__field-label', text: 'Papier' }),
    rasterSelect,
  );

  /* --- Gruppe: Schrift --- */

  const fontValue = h('span', { class: 'board-bar__value' });

  /** Schriftgroesse aendern, in sinnvollen Schritten statt Pixel fuer Pixel. */
  function stepFont(delta: number): void {
    const object = canvas.selected();
    if (!object) return;
    const current = object.fontSize ?? defaultFontSize(object.type);
    const next = Math.min(MAX_FONT, Math.max(MIN_FONT, Math.round(current + delta)));
    canvas.updateSelected({ fontSize: next });
    fontValue.textContent = `${next}`;
  }

  const boldBtn = button('B', () => toggleStyle('bold'), {
    class: 'board-bar__btn board-bar__btn--bold',
    title: 'Fett',
  });
  const italicBtn = button('I', () => toggleStyle('italic'), {
    class: 'board-bar__btn board-bar__btn--italic',
    title: 'Kursiv',
  });

  function toggleStyle(which: 'bold' | 'italic'): void {
    const object = canvas.selected();
    if (!object) return;
    const next = !object[which];
    canvas.updateSelected({ [which]: next });
    (which === 'bold' ? boldBtn : italicBtn).classList.toggle('board-bar__btn--on', next);
  }

  const textColor = colorField('Schrift', (value) => canvas.updateSelected({ color: value }));

  const fontGroup = h(
    'div',
    { class: 'board-bar__group board-bar__group--object' },
    button('A−', () => stepFont(-2), { class: 'board-bar__btn', title: 'Kleiner' }),
    fontValue,
    button('A+', () => stepFont(2), { class: 'board-bar__btn', title: 'Größer' }),
    boldBtn,
    italicBtn,
    textColor.wrap,
  );

  /* --- Gruppe: Form und Flaeche --- */

  const shapeToggle = button('◯', () => {
    const object = canvas.selected();
    if (!object) return;
    const next = object.shape === 'ellipse' ? 'rect' : 'ellipse';
    canvas.updateSelected({ shape: next });
    shapeToggle.textContent = next === 'ellipse' ? '▭' : '◯';
    shapeToggle.title = next === 'ellipse' ? 'Zu Rechteck' : 'Zu Ellipse';
  }, { class: 'board-bar__btn', title: 'Zu Ellipse' });

  const fillColor = colorField('Füllung', (value) => canvas.updateSelected({ fill: value }));
  const strokeColor = colorField('Linie', (value) => canvas.updateSelected({ stroke: value }));

  const clearFill = button('Ohne', () => canvas.updateSelected({ fill: '' }), {
    class: 'board-bar__btn',
    title: 'Füllung entfernen',
  });

  const shapeGroup = h(
    'div',
    { class: 'board-bar__group board-bar__group--object' },
    shapeToggle,
    fillColor.wrap,
    clearFill,
    strokeColor.wrap,
  );

  /* --- Gruppe: Verbinden und Loeschen --- */

  const linkBtn = button('Verbinden', () => {
    canvas.startLink();
    linkBtn.classList.add('board-bar__btn--on');
    linkBtn.textContent = 'Ziel wählen …';
  }, { class: 'board-bar__btn', title: 'Mit einem anderen Objekt verbinden' });

  const deleteBtn = button('Löschen', () => canvas.deleteSelected(), {
    class: 'board-bar__btn board-bar__btn--danger',
    title: 'Auswahl löschen',
  });

  const actionGroup = h('div', { class: 'board-bar__group board-bar__group--object' }, linkBtn, deleteBtn);

  /* --- Gruppe: Verbindung --- */

  const edgeColor = colorField('Linie', (value) => canvas.updateSelected({ stroke: value }));
  const edgeGroup = h(
    'div',
    { class: 'board-bar__group board-bar__group--object' },
    h('span', { class: 'board-bar__field-label', text: 'Verbindung' }),
    edgeColor.wrap,
    button('Löschen', () => canvas.deleteSelected(), {
      class: 'board-bar__btn board-bar__btn--danger',
      title: 'Verbindung löschen',
    }),
  );

  /* --- Gruppe: Zoom (immer sichtbar) --- */

  const zoomGroup = h(
    'div',
    { class: 'board-bar__group board-bar__group--zoom' },
    button('−', () => canvas.zoomBy(1 / 1.25), { class: 'board-bar__btn', title: 'Herauszoomen' }),
    button('Alles', () => canvas.fit(), { class: 'board-bar__btn', title: 'Alles ins Bild' }),
    button('+', () => canvas.zoomBy(1.25), { class: 'board-bar__btn', title: 'Hineinzoomen' }),
  );

  bar.append(insertGroup, fontGroup, shapeGroup, actionGroup, edgeGroup, zoomGroup);

  /**
   * Blendet die Gruppen zur Auswahl ein und setzt ihre Werte.
   * Hier wird NICHTS neu gebaut – siehe Hinweis oben im Dateikopf.
   */
  function syncBar(object: CanvasObject | null, edge: CanvasEdge | null): void {
    // Der Verbindungsmodus endet mit jeder neuen Auswahl.
    if (!canvas.linking()) {
      linkBtn.classList.remove('board-bar__btn--on');
      linkBtn.textContent = 'Verbinden';
    }

    insertGroup.hidden = object !== null || edge !== null;
    edgeGroup.hidden = edge === null;
    actionGroup.hidden = object === null;
    fontGroup.hidden = object === null || !TEXTY.has(object.type);
    shapeGroup.hidden = object === null || object.type !== 'shape';

    if (edge) {
      edgeColor.input.value = asHex(edge.color, '#8a8a8a');
      return;
    }
    if (!object) return;

    fontValue.textContent = String(object.fontSize ?? defaultFontSize(object.type));
    boldBtn.classList.toggle('board-bar__btn--on', object.bold === true);
    italicBtn.classList.toggle('board-bar__btn--on', object.italic === true);
    textColor.input.value = asHex(object.color, '#1c1c1e');

    if (object.type === 'shape') {
      const ellipse = object.shape === 'ellipse';
      shapeToggle.textContent = ellipse ? '▭' : '◯';
      shapeToggle.title = ellipse ? 'Zu Rechteck' : 'Zu Ellipse';
      fillColor.input.value = asHex(object.fill, '#d8d0c0');
      strokeColor.input.value = asHex(object.stroke || object.color, '#2f3e4d');
    }
  }

  syncBar(null, null);

  /* ------------------------------------------------------- Brotkrumen */

  const crumbs = h(
    'nav',
    { class: 'crumbs', attrs: { 'aria-label': 'Pfad' } },
    h('button', {
      class: 'crumbs__link',
      text: 'Band-Bibel',
      attrs: { type: 'button' },
      on: { click: () => navigate('#/bible') },
    }),
    h('span', { class: 'crumbs__sep', text: '›' }),
    h('button', {
      class: 'crumbs__link',
      text: room?.name || 'Raum',
      attrs: { type: 'button' },
      on: { click: () => navigate(`#/bible/room/${board.roomId}`) },
    }),
    h('span', { class: 'crumbs__sep', text: '›' }),
    h('span', { class: 'crumbs__here', text: boardTitle(board) || 'Neue Fläche' }),
  );

  const body = h('div', { class: 'board-page' }, crumbs, canvas.element, bar);

  return {
    title: boardTitle(board) || 'Neue Fläche',
    subtitle: room?.name,
    back: `#/bible/room/${board.roomId}`,
    flush: true,
    actions: [
      status,
      rasterField,
      h(
        'button',
        {
          class: 'btn btn--icon',
          attrs: { type: 'button', title: 'Titel ändern', 'aria-label': 'Titel ändern' },
          on: {
            click: async () => {
              const title = await askText({
                title: 'Titel ändern',
                label: 'Titel der Fläche',
                value: boardTitle(board),
                confirm: 'Speichern',
                allowEmpty: true,
              });
              if (title === null) return;
              await flush();
              board = await saveBoard({ ...board, title });
              refresh();
            },
          },
        },
        icon('pencil'),
      ),
      h(
        'button',
        {
          class: 'btn btn--icon',
          attrs: { type: 'button', title: 'Fläche löschen', 'aria-label': 'Fläche löschen' },
          on: {
            click: async () => {
              if (!confirm(`"${boardTitle(board) || 'Neue Fläche'}" löschen?`)) return;
              pending = null;
              clearTimeout(saveTimer);
              const roomId = board.roomId;
              await deleteBoard(board.id);
              toast('Fläche gelöscht');
              navigate(`#/bible/room/${roomId}`);
            },
          },
        },
        icon('trash'),
      ),
    ],
    body,
    // Die Welt ist die Inhaltsflaeche, der Zoomfaktor die Einheit – damit
    // sitzt die Handschrift unabhaengig von Zoom und Verschiebung richtig.
    drawing: {
      id: boardSurfaceId(board.id),
      host: canvas.drawing.host,
      content: canvas.drawing.content,
      getUnit: canvas.drawing.getUnit,
    },
    cleanup: () => {
      clearTimeout(saveTimer);
      canvas.destroy();
      void flush();
    },
  };
}
